import React from "react";
import { Shape, ZoneShape } from "../../../types";
import {
  absoluteRegionZones,
  collectSpots,
  findRegionConflicts,
  type PlacedSpot,
  prepareRegionsForSave
} from "../../../utils/zones";
import { useMapCanvasShapes } from "../contexts/MapCanvasContext";

export interface ZoneChecks {
  /** Regions with containment, area and nesting filled in, as on save. */
  regions: ZoneShape[];
  /** Regions overlapping another of the same class without nesting. */
  conflicting: ZoneShape[];
  /** Open areas without an organisation zone. */
  untagged: ZoneShape[];
  /** Spots (with their centres) in no region at all. */
  unplaced: PlacedSpot[];
}

const EMPTY: ZoneChecks = {
  regions: [],
  conflicting: [],
  untagged: [],
  unplaced: []
};

const SETTLE_MS = 250;

// One result per shapes array, shared by every consumer (the Zones panel
// and the canvas overlay) so the containment maths runs once.
const cache = new WeakMap<Shape[], ZoneChecks>();

const computeZoneChecks = (shapes: Shape[]): ZoneChecks => {
  const cached = cache.get(shapes);
  if (cached) return cached;

  const prepared = prepareRegionsForSave(shapes);
  // Grouped regions included, at their canvas positions for the overlay.
  const regions = absoluteRegionZones(prepared);
  if (regions.length === 0) {
    cache.set(shapes, EMPTY);
    return EMPTY;
  }

  const conflictIds = new Set(
    findRegionConflicts(prepared).flatMap((conflict) => conflict.zoneIds)
  );
  const placed = new Set<string>();
  for (const region of regions) {
    for (const spotId of region.containedSpotIds ?? []) placed.add(spotId);
  }

  const result: ZoneChecks = {
    regions,
    conflicting: regions.filter((region) => conflictIds.has(region.id)),
    untagged: regions.filter(
      (region) =>
        !region.zone_id &&
        region.zoneType !== "room" &&
        region.zoneType !== "corridor"
    ),
    unplaced: collectSpots(shapes).filter((spot) => !placed.has(spot.id))
  };
  cache.set(shapes, result);
  return result;
};

export const EMPTY_ZONE_CHECKS = EMPTY;

export interface ZoneChecksWatcherProps {
  enabled?: boolean;
  onChange: (checks: ZoneChecks) => void;
}

/**
 * Watches the shapes and hands out fresh zone checks once they have stopped
 * changing for a moment. It renders nothing: mounting it inside a panel or
 * overlay keeps that component off the per-frame shapes subscription, so
 * only this null component re-renders while a shape is dragged, and the
 * host re-renders when the checks actually change. Dragging a desk across
 * the floor updates the shapes on every mouse move, and polygon containment
 * for every desk against every region is too much work per frame.
 */
export const ZoneChecksWatcher: React.FC<ZoneChecksWatcherProps> = ({
  enabled = true,
  onChange
}) => {
  const { shapes } = useMapCanvasShapes();

  React.useEffect(() => {
    if (!enabled) return;
    const timer = window.setTimeout(
      () => onChange(computeZoneChecks(shapes)),
      SETTLE_MS
    );
    return () => window.clearTimeout(timer);
  }, [shapes, enabled, onChange]);

  return null;
};
